import CrunchAlphaAssistant from './components/CrunchAlphaAssistant';
import { PartnerProvider } from './context/PartnerContext';
import React, { useState, useEffect } from "react";
import NotFound from './pages/NotFound';
import AcademyHome from './pages/academy/AcademyHome';
import AcademyCourse from './pages/academy/AcademyCourse';
import AcademyLesson from './pages/academy/AcademyLesson';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import MainLayout from "./layouts/MainLayout";
import PublicLayout from "./layouts/PublicLayout";

// Public
import LandingPage from "./pages/public/LandingPage";
import AffiliateDashboard from "./pages/affiliate/AffiliateDashboard";
import PricingTiers from "./pages/public/PricingTiers";

// Auth
import LoginPage from "./pages/auth/Login";
import RegisterPage from "./pages/auth/Register";
import VerifyEmailPage from "./pages/auth/VerifyEmail";
import ImpersonatePage from "./pages/auth/ImpersonatePage";
import CheckEmailPage from "./pages/auth/CheckEmail";
import OnboardingRole from "./pages/auth/OnboardingRole";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

// Broker
import OpenAccount from "./pages/broker/OpenAccount";
import BrokerMarketplace from "./pages/broker/BrokerMarketplace";
import LinkAccount from "./pages/broker/LinkAccount";

// Legal
import TermsPage from "./pages/legal/Terms";
import PrivacyPage from "./pages/legal/Privacy";
import RiskPage from "./pages/legal/Risk";
import AboutUs from "./pages/public/AboutUs";

// App - core
import TraderDashboard from "./pages/trader/TraderDashboard";
import TraderPerformance from "./pages/trader/TraderPerformance";
import InvestorDashboard from "./pages/investor/InvestorDashboard";
import InvestorAllocation from "./pages/investor/InvestorAllocation";
import InvestorSubscriptions from "./pages/investor/InvestorSubscriptions";
import InvestorBilling from "./pages/investor/InvestorBilling";
import InvestorAccountPerformance from "./pages/investor/InvestorAccountPerformance";
import { InvestorPerformanceFees } from "./pages/investor/InvestorBilling";
import AnalystDashboard from "./pages/analyst/AnalystDashboard";
import TraderEarnings from "./pages/trader/TraderEarnings";
import AnalystEarnings from "./pages/analyst/AnalystEarnings";
import LeaderboardPage from "./pages/leaderboard/LeaderboardPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AnalystApplications from "./pages/admin/AnalystApplications";

// Other pages
import MarketplacePage from "./pages/marketplace/MarketplacePage";
import TraderProfile from "./pages/marketplace/TraderProfile";
import PublicStrategyPage from "./pages/public/PublicStrategyPage";
import PublicAnalystPage from "./pages/public/PublicAnalystPage";
import AnalystProfile from "./pages/marketplace/AnalystProfile";
import SignalsPage from "./pages/signals/SignalsPage";
import TradeHistoryPage from "./pages/trade-history/TradeHistoryPage";
import CashflowPage from "./pages/cashflow/CashflowPage";
import ToolsPage from "./pages/tools/ToolsPage";
import AnalyticsPage from "./pages/analytics/AnalyticsPage";
import ProfilePage from "./pages/profile/ProfilePage";
import QAPage from "./pages/qa/QAPage";
import PartnerAdminRoute from "./pages/partner/PartnerAdminRoute";

// ── ProtectedRoute ──────────────────────────────────────────
// allowedRoles: array of roles allowed, e.g. ["trader"]
// If no token → redirect to /login
// If wrong role → redirect to their own dashboard
const getHomePath = (role) => {
  if (role === "investor") return "/investor";
  if (role === "analyst")  return "/analyst";
  if (role === "admin")    return "/admin";
  return "/trader";
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token");
  const role  = (localStorage.getItem("user_role") || "trader").toLowerCase();

  if (!token) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={getHomePath(role)} replace />;
  }

  return children;
};

// Shared = accessible by all logged-in users
const ALL_ROLES = ["trader", "investor", "analyst", "admin"];

const ImpersonateBanner = () => {
  const [email, setEmail] = React.useState(localStorage.getItem("impersonate_as_email"));
  const [adminToken, setAdminToken] = React.useState(localStorage.getItem("impersonate_admin_token"));

  React.useEffect(() => {
    const check = () => {
      setEmail(localStorage.getItem("impersonate_as_email"));
      setAdminToken(localStorage.getItem("impersonate_admin_token"));
    };
    window.addEventListener("focus", check);
    return () => window.removeEventListener("focus", check);
  }, []);

  if (!email || !adminToken) return null;

  const exitImpersonate = async () => {
    // Logout session impersonate dulu (invalidate token ruben di backend)
    const currentToken = localStorage.getItem("access_token");
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${currentToken}`
        },
        body: JSON.stringify({ refresh_token: localStorage.getItem("refresh_token") || "" })
      });
    } catch(e) { /* ignore logout error */ }

    // Restore admin session
    localStorage.setItem("access_token", adminToken);
    localStorage.setItem("user_role", "admin");
    localStorage.setItem("is_admin", "true");
    localStorage.removeItem("impersonate_admin_token");
    localStorage.removeItem("impersonate_as_email");
    localStorage.removeItem("refresh_token");
    window.location.href = "/admin";
  };

  return (
    <div style={{
      position:"fixed", top:0, left:0, right:0, zIndex:99999,
      background:"var(--warning)", color:"var(--bg-surface)",
      padding:"8px 20px", display:"flex", alignItems:"center",
      justifyContent:"space-between", fontSize:13, fontWeight:600
    }}>
      <span>Viewing as <strong>{email}</strong> - Admin impersonate session</span>
      <button onClick={exitImpersonate} style={{
        padding:"4px 16px", borderRadius:6, border:"none",
        background:"var(--bg-surface)", color:"var(--warning)", cursor:"pointer",
        fontWeight:700, fontSize:12
      }}>Exit & Back to Admin</button>
    </div>
  );
};


const App = () => {
  return (
    <PartnerProvider>
    <BrowserRouter>
      <ImpersonateBanner />
      <Routes>
        {/* PUBLIC SHELL */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
          <Route path="/check-email" element={<CheckEmailPage />} />
          <Route path="/onboarding/role" element={<OnboardingRole />} />
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/broker/open-account" element={<OpenAccount />} />
          <Route path="/brokers" element={<BrokerMarketplace />} />
          <Route path="/broker/link-account" element={<LinkAccount />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/risk" element={<RiskPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/risk" element={<RiskPage />} />
          <Route path="/qa" element={<QAPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/strategy/:accountId" element={<PublicStrategyPage />} />
          <Route path="/strategy/analyst/:setId" element={<PublicAnalystPage />} />
        </Route>

        {/* APP SHELL */}
        <Route element={<MainLayout />}>

          {/* Trader only */}
          <Route path="/trader/earnings" element={<ProtectedRoute allowedRoles={["trader","admin"]}><TraderEarnings /></ProtectedRoute>} />
          <Route path="/trader" element={
            <ProtectedRoute allowedRoles={["trader","admin"]}>
              <TraderDashboard />
            </ProtectedRoute>
          } />
          <Route path="/trader/performance" element={
            <ProtectedRoute allowedRoles={["trader","admin"]}>
              <TraderPerformance />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute allowedRoles={["trader","admin"]}>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/tools" element={
            <ProtectedRoute allowedRoles={["trader","admin"]}>
              <ToolsPage />
            </ProtectedRoute>
          } />
          <Route path="/trade-history" element={
            <ProtectedRoute allowedRoles={["trader","admin"]}>
              <TradeHistoryPage />
            </ProtectedRoute>
          } />

          {/* Investor only */}
          <Route path="/investor" element={
            <ProtectedRoute allowedRoles={["investor","admin"]}>
              <InvestorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/affiliate" element={
            <ProtectedRoute allowedRoles={["trader","investor","analyst","admin"]}>
              <AffiliateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/investor/allocation" element={
            <ProtectedRoute allowedRoles={["investor","admin"]}>
              <InvestorAllocation />
            </ProtectedRoute>
          } />
          <Route path="/investor/subscriptions" element={
            <ProtectedRoute allowedRoles={["investor","admin"]}>
              <InvestorSubscriptions />
            </ProtectedRoute>
          } />
          <Route path="/investor/account-performance" element={<ProtectedRoute allowedRoles={["investor","admin"]}><InvestorAccountPerformance /></ProtectedRoute>} />
          <Route path="/investor/performance-fees" element={<ProtectedRoute allowedRoles={["investor","admin"]}><Navigate to="/investor/billing" replace /></ProtectedRoute>} />
          <Route path="/investor/billing" element={
            <ProtectedRoute allowedRoles={["investor","admin"]}>
              <InvestorBilling />
            </ProtectedRoute>
          } />

          {/* Analyst only */}
          <Route path="/analyst/earnings" element={<ProtectedRoute allowedRoles={["analyst","admin"]}><AnalystEarnings /></ProtectedRoute>} />
          <Route path="/analyst" element={
            <ProtectedRoute allowedRoles={["analyst","admin"]}>
              <AnalystDashboard />
            </ProtectedRoute>
          } />

          {/* Admin only */}
          <Route path="/admin/analyst-applications" element={<ProtectedRoute allowedRoles={["admin"]}><AnalystApplications /></ProtectedRoute>} />
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          } />

          {/* Shared - all roles */}
          <Route path="/marketplace" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <MarketplacePage />
            </ProtectedRoute>
          } />
          <Route path="/trader/:accountId" element={<TraderProfile />} />
          <Route path="/marketplace/trader/:accountId" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <TraderProfile />
            </ProtectedRoute>
          } />
          <Route path="/marketplace/analyst/:setId" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <AnalystProfile />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <ProfilePage />
            </ProtectedRoute>
          } />

          {/* Legacy routes - redirect to dashboard */}
          <Route path="/signals" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <SignalsPage />
            </ProtectedRoute>
          } />
          <Route path="/cashflow" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <CashflowPage />
            </ProtectedRoute>
          } />
          <Route path="/leaderboard" element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <LeaderboardPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Fallback */}
        <Route path="/academy" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AcademyHome />
            </ProtectedRoute>
          } />
          <Route path="/academy/courses/:slug" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AcademyCourse />
            </ProtectedRoute>
          } />
          <Route path="/academy/lessons/:id" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AcademyLesson />
            </ProtectedRoute>
          } />
        <Route path="/partner-admin" element={<PartnerAdminRoute><AdminDashboard /></PartnerAdminRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    <CrunchAlphaAssistant />
  </BrowserRouter>
    </PartnerProvider>
  );
};

export default App;
